
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Crown } from "lucide-react";
import KarmaCard from "./KarmaCard";
import FollowStats from "./FollowStats";
import PremiumBadge from "./PremiumBadge";
import { useProfileStats } from "./useProfileStats";
import FollowersDialog from "./followers/FollowersDialog";
import FollowingDialog from "./followers/FollowingDialog";

interface ProfileStatsProps {
  karmaPoints: number;
  karmaLevel: number;
  followersCount: number;
  followingCount: number;
  loading: boolean;
  isAdmin?: boolean;
}

const ProfileStats = ({ 
  karmaPoints, 
  karmaLevel, 
  followersCount, 
  followingCount, 
  loading,
  isAdmin = false
}: ProfileStatsProps) => {
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const { 
    followers, 
    following, 
    loadingFollowers, 
    loadingFollowing, 
    loadFollowers, 
    loadFollowing 
  } = useProfileStats();

  // Load followers data when dialog opens
  useEffect(() => {
    if (showFollowers && followers.length === 0) {
      loadFollowers();
    }
  }, [showFollowers]);

  // Load following data when dialog opens
  useEffect(() => {
    if (showFollowing && following.length === 0) {
      loadFollowing();
    }
  }, [showFollowing]);

  return (
    <>
      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              when: "beforeChildren",
              staggerChildren: 0.1,
              delayChildren: 0.2
            }
          }
        }}
      >
        <KarmaCard karmaPoints={karmaPoints || 0} karmaLevel={karmaLevel || 1} />
        <FollowStats 
          followersCount={followersCount} 
          followingCount={followingCount} 
          loading={loading}
          onViewFollowers={() => setShowFollowers(true)}
          onViewFollowing={() => setShowFollowing(true)} 
        />
      </motion.div>
      
      {isAdmin && (
        <div className="flex items-center mt-4">
          <Badge variant="outline" className="bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border-amber-300 flex items-center gap-1 px-3 py-1">
            <Crown className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
            Admin
          </Badge>
        </div>
      )}

      {!isAdmin && karmaLevel >= 3 && (
        <div className="mt-4">
          <PremiumBadge karmaLevel={karmaLevel} />
        </div>
      )}

      <FollowersDialog 
        open={showFollowers} 
        onClose={() => setShowFollowers(false)} 
        followers={followers}
        loading={loadingFollowers}
      />

      <FollowingDialog 
        open={showFollowing} 
        onClose={() => setShowFollowing(false)} 
        following={following}
        loading={loadingFollowing}
      />
    </>
  );
};

export default ProfileStats;
